import type { MerrRequest } from '@setplex/merr'
import type { RequestInput } from '@setplex/tria-api'
import { RequestBuckets } from './buckets'
import {
  BACKOFF_FACTOR,
  BACKOFF_LIMIT_MILLIS,
  INTERVAL_MILLIS,
} from './index.h'
import { colorFromTimestamp, colored, weightFromColor } from './colors'
import {
  calculateBackoffMillis,
  getBucketName,
  getSignal,
  toURL,
  logger,
  format,
} from './lib'

class RequestPolicyTrialError extends Error {
  constructor(url: URL) {
    super(`Request Policy cancelled request to ${url.href}`)
  }
}

export function isTrialError(error: unknown): error is RequestPolicyTrialError {
  return error instanceof RequestPolicyTrialError
}

/**
 * Request Policy implementation
 * @see https://setplexapps.atlassian.net/wiki/spaces/MW/pages/3430776834/Request+Policy
 */
export class RequestPolicy {
  public buckets: RequestBuckets

  constructor(public persistKey = 'tria__request_policy_state') {
    this.buckets = new RequestBuckets(persistKey)
    this.buckets.debugPrint('loaded buckets:')
    this.cleanup()
  }

  /**
   * Get current Request Policy status,
   * either it is enforced for some request groups or not
   */
  isEnforced() {
    return !this.buckets.isEmpty()
  }

  /**
   * Get bucket for the request
   */
  getBucket(request: Request | RequestInput | string) {
    const url = toURL(request)
    const bucketName = getBucketName(url)
    const bucket = this.buckets.get(bucketName)
    return { url, bucketName, bucket }
  }

  /**
   * Trial request to check if it is allowed by Request Policy or not
   */
  trial(
    input: RequestInput | MerrRequest,
    limit?: number,
    latency?: number
  ): void | Promise<void> {
    const { url, bucketName, bucket } = this.getBucket(input)
    const surl = url ? url.pathname + url.search : '-'

    // pass if there is no bucket
    if (!bucket) {
      logger.debug(`✅ trial %c${surl}%c in bucket %c${bucketName}%c (no bucket)`, 'color:teal', 'color:inherit', 'color:red', 'color:inherit') // biome-ignore format: one line
      return
    }

    const timeLeft = bucket.limit - Date.now()

    // pass if time left < 0, meaning that current time > limit
    if (timeLeft < 0) {
      logger.debug(`✅ trial %c${surl}%c in bucket %c${bucketName}%c (limit was until %c${format(bucket.limit)}%c)`, 'color:teal', 'color:inherit', 'color:red', 'color:inherit', 'color:gray', 'color:inherit') // biome-ignore format: one line
      return
    }

    // if request is aborted already - trial is not needed
    const signal = getSignal(input)
    if (signal?.aborted) {
      logger.debug(`✅ trial %c${surl}%c in bucket %c${bucketName}%c (request was aborted)`, 'color:teal', 'color:inherit', 'color:red', 'color:inherit') // biome-ignore format: one line
      return
    }

    // return promise that resolves after the time left,
    // this allows to wait a bit if there is a little time left,
    // without blocking the request right away
    if (limit != null) {
      const maxDelay = limit - (latency || 0)
      if (timeLeft < maxDelay) {
        logger.debug(`⏳ trial request %c${surl}%c in bucket %c${bucketName}%c (postpone for ${maxDelay}ms)`, 'color:teal', 'color:inherit', 'color:red', 'color:inherit') // biome-ignore format: one line
        return new Promise((resolve, reject) => {
          const abort = () => resolve(clearTimeout(timeout))

          // eslint-disable-next-line no-var
          var timeout = setTimeout(() => {
            signal?.removeEventListener('abort', abort)

            // after timeout is reached, trial request again
            // because conditions might have changed
            // (but do not specify `limit` this time, this is "one time opportunity")
            Promise.try(() => this.trial(input))
              .then(resolve)
              .catch(reject)
          }, maxDelay)

          // resolve promise on abort signal
          // this allows to cancel the trial if request is aborted before the timeout
          signal?.addEventListener('abort', abort, { once: true })
        })
      }
    }

    // otherwise trial is not passed, request should be blocked
    logger.debug(`⛔ trial %c${surl}%c in bucket %c${bucketName}%c (shall not pass 🧙 until %c${format(bucket.limit)}%c)`, 'color:teal', 'color:inherit', 'color:red', 'color:inherit', 'color:orange', 'color:inherit') // biome-ignore format: one line
    throw new RequestPolicyTrialError(url!)
  }

  /**
   * Handle successful API response
   * Successful response implies that response exists
   * @see https://setplexapps.atlassian.net/wiki/spaces/MW/pages/3430776834/Request+Policy#After-Successful-Request
   */
  success(request: Request | MerrRequest | string): void {
    const { url, bucketName, bucket } = this.getBucket(request)
    const surl = url ? url.pathname + url.search : '-'

    // do nothing if there is no bucket
    if (!bucket) return

    // check successes count
    // if successes >= 1, remove the bucket - full recovery
    if (bucket.successes >= 1) {
      logger.debug(`🧹 successful %c${surl}%c in bucket %c${bucketName}%c (remove bucket)`, 'color:teal', 'color:inherit', 'color:red', 'color:inherit') // biome-ignore format: one line
      this.buckets.delete(bucketName)
    }

    // otherwise, update the bucket with reduced backoff and increased successes
    else {
      logger.debug(`👁️ successful %c${surl}%c in bucket %c${bucketName}%c (prepare to remove bucket)`, 'color:teal', 'color:inherit', 'color:red', 'color:inherit') // biome-ignore format: one line
      bucket.successes += 1 // increment successes by 1
      bucket.backoff = Math.max(
        bucket.backoff / BACKOFF_FACTOR,
        INTERVAL_MILLIS
      ) // reduce backoff
      bucket.limit = Date.now() // allow immediate requests
      this.buckets.set(bucketName, bucket)
    }
  }

  /**
   * Handle failed API request
   * Request could be undefined if Merr fails to construct it
   * @see https://setplexapps.atlassian.net/wiki/spaces/MW/pages/3430776834/Request+Policy#After-Failed-Request
   */
  failure(request: Request | MerrRequest | string, retryAfter = 0): void {
    const now = Date.now()
    let { url, bucketName, bucket } = this.getBucket(request) // eslint-disable-line prefer-const
    const surl = url ? url.pathname + url.search : '-'

    // colored timestamp of the request, and its weight
    const failure = colored(request)
    const color = colorFromTimestamp(failure)
    const weight = weightFromColor(color)

    // no existing bucket -> create new bucket
    if (bucket == null) {
      const glogger = logger.debugGroup(`🚫 failed %c${surl}%c in bucket %c${bucketName}%c (create bucket)`, 'color:teal', 'color:inherit', 'color:red', 'color:inherit') // biome-ignore format: one line
      glogger?.debug(`request: color = ${color}, weight = ${weight}, retryAfter = ${retryAfter}`) // biome-ignore format: one line

      const backoff = calculateBackoffMillis(weight, glogger)
      bucket = {
        backoff,
        limit: Math.max(now + backoff, now + retryAfter),
        successes: 0,
        failures: [failure], // add failure failure
      }

      glogger?.debug(`-> create bucket until ${format(bucket.limit)}`) // biome-ignore format: one line
      glogger?.groupEnd()
    }

    // existing bucket -> update it with new failure
    else {
      const glogger = logger.debugGroup(`🚫 failed %c${surl}%c in bucket %c${bucketName}%c (prolong bucket)`, 'color:teal', 'color:inherit', 'color:red', 'color:inherit') // biome-ignore format: one line
      glogger?.debug(`request: color = ${color}, weight = ${weight}, retryAfter = ${retryAfter}`) // biome-ignore format: one line

      const window = (BACKOFF_FACTOR + 1) * bucket.backoff
      const windowStart = now - window
      const weights = bucket.failures.reduce((w, t) => (w + (t >= windowStart ? weightFromColor(colorFromTimestamp(t)) : 0)), 0) // biome-ignore format: one line
      glogger?.debug(`window: size = ${window}, start = ${format(windowStart)}, weight = ${weights}`) // biome-ignore format: one line

      const backoff = calculateBackoffMillis(weights + weight, glogger)
      bucket = {
        backoff,
        limit: Math.max(now + backoff, now + retryAfter),
        successes: 0, // reset successes counter
        failures: bucket.failures.concat(failure), // add current failure
      }

      glogger?.debug(`-> prolong bucket until ${format(bucket.limit)}`) // biome-ignore format: one line
      glogger?.groupEnd()
    }

    this.buckets.set(bucketName, bucket)
    this.cleanup() // cleanup buckets
  }

  cleanup(): void {
    const now = Date.now()
    const depth = (BACKOFF_FACTOR + 1) * BACKOFF_LIMIT_MILLIS
    const edge = now - depth
    let cleaned = false
    for (const [bucketName, bucket] of this.buckets.all()) {
      bucket.failures = bucket.failures.filter((t) => t >= edge) // clean up failures older than max window
      if (bucket.failures.length === 0 && bucket.limit <= now) {
        logger.debug(`🧹 cleanup bucket %c${bucketName}%c (all failures have expired)`, 'color:red', 'color:inherit') // biome-ignore format: one line
        this.buckets.delete(bucketName) // remove bucket if no failures left and limit is reached
        cleaned = true
      } else {
        this.buckets.set(bucketName, bucket)
      }
    }
    if (cleaned) this.buckets.debugPrint('updated buckets:')
  }
}
